import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Card, EmptyState, Mono, PageBody, PageHeader } from "../ui/Field";

type Entry = { id: string; ts: number; actor_id: string | null; actor_email: string | null; site_id: string | null; action: string; target_kind: string | null; target_id: string | null; meta: string | null };

export default function AuditLog() {
  const { slug } = useParams();
  const [items, setItems] = useState<Entry[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const url = slug ? `/api/account/audit?site=${encodeURIComponent(slug)}&limit=200` : "/api/account/audit?limit=200";
    api.get<{ items: Entry[] }>(url)
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  return (
    <PageBody>
      <PageHeader
        eyebrow={`№ 13 · History${slug ? ` · ${slug}` : ""}`}
        title={<>Audit <em>log</em></>}
        description="Append-only record of administrative actions across pages, entries, settings, and access. Most recent first; up to 200 entries."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="A1" title={`Recent activity (${items.length})`} bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState title="No entries." description="As you and your team act, every change is recorded here." />
        ) : (
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left label-tracked border-b border-[var(--color-rule)]">
                <th className="px-5 py-2.5 w-44">Timestamp</th>
                <th className="px-5 py-2.5 w-56">Actor</th>
                <th className="px-5 py-2.5 w-48">Action</th>
                <th className="px-5 py-2.5">Target</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-[var(--color-rule)] last:border-0 hover:bg-[var(--color-paper-2)] transition-colors">
                  <td className="px-5 py-2 text-[11.5px] text-[var(--color-muted)] font-mono whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                  <td className="px-5 py-2 text-[var(--color-ink)] truncate">{e.actor_email ?? <span className="text-[var(--color-muted)] italic">system</span>}</td>
                  <td className="px-5 py-2"><Badge tone="accent">{e.action}</Badge></td>
                  <td className="px-5 py-2 text-[11.5px] text-[var(--color-muted)] font-mono truncate">
                    {e.target_kind ? <><Mono>{e.target_kind}</Mono>:<Mono>{e.target_id?.slice(-8)}</Mono></> : ""}
                    {e.meta ? ` ${e.meta.slice(0, 80)}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PageBody>
  );
}
